import {
  Checkbox,
  Input,
  message,
  notification,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Table,
} from 'antd'
import { Button, DeleteAndUpdate, EmptyScreen, Typography } from 'modules/core/components'
import { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import configuration from 'modules/configuration'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { generateDetailedMessageError } from 'modules/core/utils'

const flagText = (value, t) => (value ? t('YES') : t('NO'))

const SubtypesTable = ({ documentType, isSaving, onChange, editRow, setEditRow }) => {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [requiresClosing, setRequiresClosing] = useState(true)
  const [requiresBalance, setRequiresBalance] = useState(true)
  const [requiresRelease, setRequiresRelease] = useState(true)
  const [deleteId, setDeleteId] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const { t } = useTranslation()

  const subtypes = documentType.subtypes || []

  const handleCreate = () => {
    onChange(documentType.id, [
      ...subtypes,
      {
        code: code.trim(),
        name: name.trim(),
        requires_closing_document: requiresClosing,
        requires_balance_control: requiresBalance,
        requires_release_strategy: requiresRelease,
      },
    ]).then((saved) => {
      if (!saved) return
      setCode('')
      setName('')
      setRequiresClosing(true)
      setRequiresBalance(true)
      setRequiresRelease(true)
    })
  }

  const handleDelete = (removed) => {
    setDeleteId(null)
    onChange(
      documentType.id,
      subtypes.filter((subtype) => subtype.id !== removed),
    )
  }

  const onCellEdit = (field, value) => setDataCellEdit((prev) => ({ ...prev, [field]: value }))

  const cancelEdit = () => {
    setEditRow(null)
    setDataCellEdit({})
  }

  const handleEdit = () =>
    onChange(
      documentType.id,
      subtypes.map((subtype) =>
        subtype.id === editRow ? { ...subtype, ...dataCellEdit } : subtype,
      ),
    ).then((saved) => {
      if (saved) cancelEdit()
    })

  const isEditValid = Object.entries(dataCellEdit).every(
    ([field, value]) => !['code', 'name'].includes(field) || value.trim(),
  )

  const flagColumn = (dataIndex, title, width) => ({
    dataIndex,
    title,
    width,
    render: (value, row) =>
      row.id === editRow ? (
        <Checkbox
          defaultChecked={value}
          onChange={(e) => onCellEdit(dataIndex, e.target.checked)}
        />
      ) : (
        flagText(value, t)
      ),
  })

  const columns = [
    {
      dataIndex: 'code',
      title: t('FIELD_CODE'),
      width: 160,
      render: (value, row) =>
        row.id === editRow ? (
          <Input
            value={dataCellEdit.code ?? value}
            style={{ width: 140 }}
            onChange={(e) => onCellEdit('code', e.target.value)}
          />
        ) : (
          value
        ),
    },
    {
      dataIndex: 'name',
      title: t('FIELD_NAME'),
      render: (value, row) =>
        row.id === editRow ? (
          <Input
            value={dataCellEdit.name ?? value}
            style={{ width: '100%' }}
            onChange={(e) => onCellEdit('name', e.target.value)}
          />
        ) : (
          value
        ),
    },
    flagColumn('requires_balance_control', t('CONFIG_DOCUMENT_TYPE_REQUIRES_BALANCE_CONTROL'), 170),
    flagColumn(
      'requires_release_strategy',
      t('CONFIG_DOCUMENT_TYPE_REQUIRES_RELEASE_STRATEGY'),
      190,
    ),
    flagColumn(
      'requires_closing_document',
      t('CONFIG_DOCUMENT_TYPE_REQUIRES_CLOSING_DOCUMENT'),
      220,
    ),
    {
      dataIndex: 'id',
      key: 'actions',
      title: t('FIELD_ACTIONS'),
      align: 'right',
      width: 180,
      render: (id) =>
        id === editRow ? (
          <DeleteAndUpdate
            onCancel={cancelEdit}
            onConfirm={cancelEdit}
            onSave={handleEdit}
            dataCellEdit={dataCellEdit}
            disabled={!isEditValid}
            isEditing={isSaving}
          />
        ) : (
          <Popconfirm
            placement="bottomRight"
            title={t('CONFIG_DOCUMENT_SUBTYPE_DELETE_CONFIRMATION')}
            okText={t('ACTION_DELETE')}
            onConfirm={() => handleDelete(id)}
            okButtonProps={{ loading: isSaving }}
            cancelButtonProps={{ disabled: isSaving }}
            cancelText={t('ACTION_CANCEL')}
            onCancel={() => setDeleteId(null)}
            visible={deleteId === id}
          >
            <Space size="middle">
              <Button.Icon
                title="ACTION_EDIT"
                icon={<Typography.Icon level={2} icon={EditOutlined} />}
                disabled={Boolean(editRow)}
                onClick={() => setEditRow(id)}
              />
              <Button.Icon
                title="ACTION_DELETE"
                icon={<Typography.Icon level={2} icon={DeleteOutlined} />}
                disabled={Boolean(editRow)}
                onClick={() => setDeleteId(id)}
              />
            </Space>
          </Popconfirm>
        ),
    },
  ]

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <Typography.Subtitle level={2}>
          {t('CONFIG_DOCUMENT_TYPE_SUBTYPES', { name: documentType.name })}
        </Typography.Subtitle>
      </div>
      {subtypes.length > 0 && (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={subtypes}
          pagination={false}
          style={{ marginTop: 8 }}
        />
      )}
      <Space style={{ marginTop: 12 }}>
        <Input
          value={code}
          placeholder={t('FIELD_CODE')}
          style={{ width: 160 }}
          onChange={(e) => setCode(e.target.value)}
        />
        <Input
          value={name}
          placeholder={t('FIELD_NAME')}
          style={{ width: 320 }}
          onChange={(e) => setName(e.target.value)}
        />
        <Checkbox checked={requiresBalance} onChange={(e) => setRequiresBalance(e.target.checked)}>
          {t('CONFIG_DOCUMENT_TYPE_REQUIRES_BALANCE_CONTROL')}
        </Checkbox>
        <Checkbox checked={requiresRelease} onChange={(e) => setRequiresRelease(e.target.checked)}>
          {t('CONFIG_DOCUMENT_TYPE_REQUIRES_RELEASE_STRATEGY')}
        </Checkbox>
        <Checkbox checked={requiresClosing} onChange={(e) => setRequiresClosing(e.target.checked)}>
          {t('CONFIG_DOCUMENT_TYPE_REQUIRES_CLOSING_DOCUMENT')}
        </Checkbox>
        <Button.Primary
          icon={<Typography.Icon level={2} icon={PlusOutlined} />}
          disabled={!code.trim() || !name.trim() || Boolean(editRow)}
          onClick={handleCreate}
          title="CONFIG_DOCUMENT_SUBTYPE_ADD"
        />
      </Space>
    </>
  )
}

const DocumentTypesList = ({
  fetchDocumentTypesConfig,
  documentTypesConfigList,
  editDocumentTypeConfig,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    setIsLoading(true)
    fetchDocumentTypesConfig().then(() => setIsLoading(false))
  }, [fetchDocumentTypesConfig])

  const onError = useCallback(
    (error) => {
      setIsSaving(false)
      notification.error({
        message: t('FEEDBACK_DEFAULT_ERROR'),
        description: generateDetailedMessageError(error),
        duration: 0,
      })
      return false
    },
    [t],
  )

  const onSaved = useCallback(
    () =>
      fetchDocumentTypesConfig().then(() => {
        message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
        setIsSaving(false)
        return true
      }),
    [fetchDocumentTypesConfig, t],
  )

  const handleToggleManual = (id, value) => {
    setIsSaving(true)
    editDocumentTypeConfig(id, { allows_manual_registration: value }).then(onSaved).catch(onError)
  }

  const handleChangeSubtypes = (id, subtypes) => {
    setIsSaving(true)
    return editDocumentTypeConfig(id, { subtypes }).then(onSaved).catch(onError)
  }

  const columns = [
    {
      dataIndex: 'name',
      title: t('FIELD_NAME'),
      width: 320,
      render: (value, row) =>
        row.allows_manual_registration ? (
          <Space size="small">
            {value}
            <Typography.Body level={3} type="secondary">
              {t('CONFIG_DOCUMENT_TYPE_SUBTYPES_COUNT', { total: (row.subtypes || []).length })}
            </Typography.Body>
          </Space>
        ) : (
          value
        ),
    },
    { dataIndex: 'code', title: t('FIELD_CODE'), width: 120 },
    {
      dataIndex: 'requires_balance_control',
      title: t('CONFIG_DOCUMENT_TYPE_REQUIRES_BALANCE_CONTROL'),
      width: 160,
      render: (value) => flagText(value, t),
    },
    {
      dataIndex: 'requires_release_strategy',
      title: t('CONFIG_DOCUMENT_TYPE_REQUIRES_RELEASE_STRATEGY'),
      width: 160,
      render: (value) => flagText(value, t),
    },
    {
      dataIndex: 'allows_manual_registration',
      title: t('CONFIG_DOCUMENT_TYPE_MANUAL_REGISTRATION'),
      width: 160,
      render: (value, row) => (
        <Switch
          checked={value}
          disabled={Boolean(editRow)}
          onChange={(checked) => handleToggleManual(row.id, checked)}
        />
      ),
    },
  ]

  const renderTable = () => (
    <Table
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={documentTypesConfigList}
      pagination={false}
      expandable={{
        expandedRowRender: (row) => (
          <SubtypesTable
            documentType={row}
            isSaving={isSaving}
            onChange={handleChangeSubtypes}
            editRow={editRow}
            setEditRow={setEditRow}
          />
        ),
        rowExpandable: (row) => row.allows_manual_registration,
      }}
    />
  )

  const renderEmptyScreen = () => (
    <EmptyScreen image={emptyStateImage} description={t('CONFIG_DOCUMENT_TYPES_EMPTY_STATE')} />
  )

  return (
    <Spin spinning={isLoading || isSaving}>
      {documentTypesConfigList.length > 0 ? renderTable() : renderEmptyScreen()}
    </Spin>
  )
}

const mapStateToProps = (state) => ({
  documentTypesConfigList: configuration.selectors.getDocumentTypesConfigList(state),
})

const mapDispatchToProps = {
  fetchDocumentTypesConfig: configuration.actions.fetchDocumentTypesConfig,
  editDocumentTypeConfig: configuration.actions.editDocumentTypeConfig,
}

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTypesList)
